import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Galerie.css';

// On importe les composants et données nécessaires
import FilterSidebar from '../../components/FilterSidebar/FilterSidebar';
import pb from '../../lib/pocketbase';
import { useAuth } from '../../context/AuthContext';
import Select from 'react-select';
import imageCompression from 'browser-image-compression';

function Galerie() {
  const { user } = useAuth();
  // Charger les filtres depuis sessionStorage au démarrage
  const loadFiltersFromStorage = () => {
    try {
      const savedFilters = sessionStorage.getItem('galerieFilters');
      if (savedFilters) {
        return JSON.parse(savedFilters);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des filtres:', error);
    }
    return {
      categories: [],
      colors: [],
      materials: [],
      pearlTypes: [],
      minPrice: '',
      maxPrice: ''
    };
  };

  const [filters, setFilters] = useState(loadFiltersFromStorage);
  // Charger l'ordre de tri depuis sessionStorage
  const loadSortOrderFromStorage = () => {
    try {
      const savedSortOrder = sessionStorage.getItem('galerieSortOrder');
      return savedSortOrder || 'default';
    } catch (error) {
      console.error('Erreur lors du chargement de l\'ordre de tri:', error);
      return 'default';
    }
  };

  const [sortOrder, setSortOrder] = useState(loadSortOrderFromStorage); // 'default' pourrait être par date d'ajout futurement
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [pearlTypes, setPearlTypes] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFilterOpen, setFilterOpen] = useState(false);

  // Charger la catégorie sélectionnée depuis sessionStorage
  const loadSelectedCategoryFromStorage = () => {
    try {
      const savedCategory = sessionStorage.getItem('galerieSelectedCategory');
      return savedCategory || 'Toutes';
    } catch (error) {
      console.error('Erreur lors du chargement de la catégorie:', error);
      return 'Toutes';
    }
  };

  const [selectedCategoryTab, setSelectedCategoryTab] = useState(loadSelectedCategoryFromStorage);
  // Nouvel état pour gérer l'ouverture du pop-up des catégories mobiles
  const [isCategoryPopupOpen, setCategoryPopupOpen] = useState(false);

  // États pour l'édition admin
  const [editingItems, setEditingItems] = useState({});
  const [editedItems, setEditedItems] = useState({});
  const [newImages, setNewImages] = useState({});
  const [isDragging, setIsDragging] = useState({});
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [currentEditingItem, setCurrentEditingItem] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchAndSetData = async () => {
      setLoading(true);
      try {
        const productsList = await pb.collection('products').getFullList({
          filter: 'is_sold = true',
          expand: 'category,color,material,pearl_type',
          requestKey: null
        });
        const categoriesList = await pb.collection('categories').getFullList({ 
          sort: 'name',
          requestKey: null
        });
        const colorsList = await pb.collection('colors').getFullList({ 
          sort: 'name',
          requestKey: null
        });
        const materialsList = await pb.collection('materials').getFullList({ 
          sort: 'name',
          requestKey: null
        });
        const pearlTypesList = await pb.collection('pearl_types').getFullList({ 
          sort: 'name',
          requestKey: null
        });

        if (!isMounted) return;

        setProducts(productsList);
        setCategories(categoriesList);
        setColors(colorsList);
        setMaterials(materialsList);
        setPearlTypes(pearlTypesList);
      } catch (error) {
        if (error.name === 'AbortError' || error.isAbort) {
          return; // Ignore silently
        }
        console.error("Erreur de chargement des données:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchAndSetData();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // Sauvegarder les filtres dans sessionStorage
    try {
      sessionStorage.setItem('galerieFilters', JSON.stringify(newFilters));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des filtres:', error);
    }
  };

  // Fonction pour gérer le clic sur un onglet de catégorie (desktop) ou une option du pop-up (mobile)
  const handleCategorySelect = (categoryName) => {
    setSelectedCategoryTab(categoryName);

    const selectedCategory = categories.find(cat => cat.name === categoryName);
    const categoryId = selectedCategory ? selectedCategory.id : null;

    const newFilters = {
      ...filters,
      categories: categoryId ? [categoryId] : []
    };
    setFilters(newFilters);

    // Sauvegarder la catégorie et les filtres dans sessionStorage
    try {
      sessionStorage.setItem('galerieSelectedCategory', categoryName);
      sessionStorage.setItem('galerieFilters', JSON.stringify(newFilters));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }

    setCategoryPopupOpen(false);
  };

    useEffect(() => {
    if (loading) return;
    let items = [...products];

    // Logique de filtrage
    if (filters.categories && filters.categories.length > 0) {
      items = items.filter(p => Array.isArray(p.category) && p.category.some(catId => filters.categories.includes(catId)));
    }
    if (filters.colors && filters.colors.length > 0) {
      items = items.filter(p => p.color.some(c => filters.colors.includes(c)));
    }
    if (filters.materials && filters.materials.length > 0) {
      items = items.filter(p => p.material.some(m => filters.materials.includes(m)));
    }
    if (filters.pearlTypes && filters.pearlTypes.length > 0) {
      items = items.filter(p => p.pearl_type.some(pt => filters.pearlTypes.includes(pt)));
    }
    if (filters.minPrice) {
      items = items.filter(p => p.price >= parseFloat(filters.minPrice));
    }
    if (filters.maxPrice) {
      items = items.filter(p => p.price <= parseFloat(filters.maxPrice));
    }

    // Logique de tri (ici, par nom car il n'y a pas de prix)
    if (sortOrder === 'name-asc') {
      items.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortOrder === 'name-desc') {
      items.sort((a, b) => b.name.localeCompare(a.name));
    }

    setFilteredItems(items);
  }, [products, filters, sortOrder]);

  // Vérifier si l'utilisateur est admin
  const isAdmin = user && user.role === 'admin';

  // Fonctions d'édition pour les admins
  const startEditing = (item) => {
    // Fermer tous les autres blocs d'édition en cours
    setEditingItems({ [item.id]: true });
    setEditedItems({
      [item.id]: {
        name: item.name,
        price: item.price,
        description: item.description,
        stock: item.stock,
        is_unique: item.is_unique,
        is_sold: item.is_sold,
        category: item.category || [],
        color: item.color || [],
        material: item.material || [],
        pearl_type: item.pearl_type || []
      }
    });
    setNewImages({ [item.id]: [] });
    setCurrentEditingItem(item.id);
    
    // Scroll automatique vers le bloc d'édition avec un petit délai pour laisser le DOM se mettre à jour
    setTimeout(() => {
      const editingElement = document.querySelector(`[data-editing-id="${item.id}"]`);
      if (editingElement) {
        editingElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const cancelEditing = (itemId) => {
    // Fermer l'édition d'abord
    setEditingItems(prev => {
      const newState = { ...prev };
      delete newState[itemId];
      return newState;
    });
    setEditedItems(prev => {
      const newState = { ...prev };
      delete newState[itemId];
      return newState;
    });
    setNewImages(prev => {
      const newState = { ...prev };
      delete newState[itemId];
      return newState;
    });
    setCurrentEditingItem(null);

    // Scroll vers la carte normale après que le DOM soit mis à jour
    setTimeout(() => {
      const normalCard = document.querySelector(`[data-card-id="${itemId}"]`);
      if (normalCard) {
        normalCard.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const saveItem = async (itemId) => {
    try {
      const editedItem = editedItems[itemId];
      const item = products.find(p => p.id === itemId);
      
      const formData = new FormData();
      
      // Ajouter les métadonnées
      formData.append('name', editedItem.name);
      formData.append('description', editedItem.description);
      formData.append('price', String(editedItem.price || 0));
      formData.append('stock', String(editedItem.is_unique ? 1 : (parseInt(editedItem.stock) || 0)));
      formData.append('is_unique', String(!!editedItem.is_unique));
      formData.append('is_sold', String(!!editedItem.is_sold));
      
      // Ajouter les relations (arrays)
      if (editedItem.category) editedItem.category.forEach(id => formData.append('category', id));
      if (editedItem.color) editedItem.color.forEach(id => formData.append('color', id));
      if (editedItem.material) editedItem.material.forEach(id => formData.append('material', id));
      if (editedItem.pearl_type) editedItem.pearl_type.forEach(id => formData.append('pearl_type', id));

      // Ajouter les images existantes
      const currentImages = item.image || [];
      if (currentImages.length > 0) {
        currentImages.forEach(imgName => formData.append('image', imgName));
      } else {
        formData.append('image', '');
      }

      // Première mise à jour
      let updatedRecord = await pb.collection('products').update(itemId, formData);

      // Si il y a de nouvelles images, les ajouter
      const itemNewImages = newImages[itemId] || [];
      if (itemNewImages.length > 0) {
        const addImagesFormData = new FormData();
        
        const currentProduct = await pb.collection('products').getOne(itemId);
        const existingImages = currentProduct.image || [];
        
        existingImages.forEach(imgName => addImagesFormData.append('image', imgName));
        itemNewImages.forEach(file => addImagesFormData.append('image', file));
        
        updatedRecord = await pb.collection('products').update(itemId, addImagesFormData);
      }
      
      // Recharger le produit avec les relations
      const refreshedItem = await pb.collection('products').getOne(itemId, {
        expand: 'category,color,material,pearl_type'
      });
      
      // Mettre à jour la liste des produits
      setProducts(prev => prev.map(p => p.id === itemId ? refreshedItem : p));
      
      // Nettoyer les états d'édition
      cancelEditing(itemId);
      
      alert('Produit mis à jour avec succès !');
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour du produit');
    }
  };

  const handleInputChange = (itemId, field, value) => {
    setEditedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };

  // Fonctions pour la gestion des images
  const handleFileChange = async (itemId, files) => {
    const fileArray = Array.from(files);
    
    const compressedFiles = await Promise.all(fileArray.map(async (file) => {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        initialQuality: 0.8
      };
      try {
        if (file.size / 1024 / 1024 > options.maxSizeMB) {
          const compressedFile = await imageCompression(file, options);
          return compressedFile;
        } else {
          return file;
        }
      } catch (error) {
        console.error('Erreur de compression:', error);
        return file;
      }
    }));

    setNewImages(prev => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), ...compressedFiles]
    }));
  };

  const handleFileDrop = (e, itemId) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(prev => ({ ...prev, [itemId]: false }));
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(itemId, e.dataTransfer.files);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e, itemId) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(prev => ({ ...prev, [itemId]: true }));
    }
  };

  const handleDragLeave = (e, itemId) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(prev => ({ ...prev, [itemId]: false }));
  };

  const handleDeleteNewImage = (itemId, filenameToDelete) => {
    setNewImages(prev => ({
      ...prev,
      [itemId]: (prev[itemId] || []).filter(file => file.name !== filenameToDelete)
    }));
  };

  const handleDeleteExistingImage = (itemId, filenameToDelete) => {
    setProducts(prev => prev.map(product => {
      if (product.id === itemId) {
        const updatedImages = product.image.filter(img => img !== filenameToDelete);
        return {
          ...product,
          image: updatedImages
        };
      }
      return product;
    }));
  };

  const handleSelectChange = (itemId, selectedOptions, actionMeta) => {
    const fieldName = actionMeta.name;
    const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setEditedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [fieldName]: values
      }
    }));
  };

  // Récupérer toutes les catégories uniques des produits vendus
  const allCategories = ['Toutes', ...categories.map(c => c.name)];

  return (
    <div className="galerie-container">
      <div className="galerie-header">
        <h1>Galerie des Créations</h1>
        <p className="galerie-subtitle">Un aperçu de nos pièces uniques qui ont déjà trouvé leur gardien(ne).</p>
      </div>

      <button className="mobile-filter-btn" onClick={() => setFilterOpen(true)}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>
        <span>Filtrer les créations</span>
      </button>

      <div className={`filter-overlay ${isFilterOpen ? 'show' : ''}`} onClick={() => setFilterOpen(false)}></div>

      <div className="galerie-body">
        <div className={`filter-sidebar-wrapper ${isFilterOpen ? 'open' : ''}`}>
          <FilterSidebar
            categories={categories}
            colors={colors}
            materials={materials}
            pearlTypes={pearlTypes}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClose={() => setFilterOpen(false)}
          />
        </div>

        <div className="sold-grid-wrapper">
          {/* Section pour les contrôles : Catégories (tabs ou bouton/popup) et Tri */}
          <div className="galerie-controls">
            {/* Version DESKTOP des catégories (tabs défilantes) */}
            <div className="category-tabs desktop-only">
              {allCategories.map(category => (
                <button
                  key={category}
                  className={`category-tab-btn ${selectedCategoryTab === category ? 'active' : ''}`}
                  onClick={() => handleCategorySelect(category)}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Version MOBILE des catégories (bouton pour ouvrir pop-up) */}
            <button className="category-mobile-btn mobile-only" onClick={() => setCategoryPopupOpen(true)}>
              <span>Catégories : </span>
              <strong>{selectedCategoryTab}</strong>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
            </button>

            <div className="sort-bar">
              <label htmlFor="sort-select">Trier par :</label>
              <select id="sort-select" value={sortOrder} onChange={(e) => {
                setSortOrder(e.target.value);
                // Sauvegarder l'ordre de tri dans sessionStorage
                try {
                  sessionStorage.setItem('galerieSortOrder', e.target.value);
                } catch (error) {
                  console.error('Erreur lors de la sauvegarde du tri:', error);
                }
              }}>
                <option value="default">Par défaut</option>
                <option value="name-asc">Nom : A à Z</option>
                <option value="name-desc">Nom : Z à A</option>
              </select>
            </div>
          </div>

          <div className="product-count-container">
            {!loading && (
              <p className="product-count">
                {filteredItems.length} {filteredItems.length > 1 ? 'créations trouvées' : 'création trouvée'}
              </p>
            )}
          </div>

          {/* Pop-up des catégories pour mobile */}
          <div className={`category-popup-overlay ${isCategoryPopupOpen ? 'show' : ''}`} onClick={() => setCategoryPopupOpen(false)}>
            <div className="category-popup" onClick={(e) => e.stopPropagation()}> {/* Empêche la fermeture au clic dans le popup */}
              <button className="close-popup-btn" onClick={() => setCategoryPopupOpen(false)}>
                &times;
              </button>
              <h2>Choisir une Catégorie</h2>
              <div className="category-popup-options">
                {allCategories.map(category => (
                  <button
                    key={category}
                    className={`category-option-btn ${selectedCategoryTab === category ? 'active' : ''}`}
                    onClick={() => handleCategorySelect(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>


          {loading ? (
            <p>Chargement de la galerie...</p>
          ) : filteredItems.length > 0 ? (
            <div className="sold-grid">
              {filteredItems.map(item => {
                const isEditing = editingItems[item.id];
                const editedItem = editedItems[item.id];
                const itemNewImages = newImages[item.id] || [];
                const itemIsDragging = isDragging[item.id];

                return (
                  <div key={item.id} className={`sold-card-wrapper ${isEditing ? 'editing' : ''}`}>
                    {isEditing ? (
                      <div className="sold-card editing-card" data-editing-id={item.id}>
                        {/* Section de gestion des images en mode édition */}
                        <div className="image-management-section">
                          <h4>Gestion des images</h4>
                          
                          {/* Zone de drop pour nouvelles images */}
                          <div 
                            className={`image-drop-zone ${itemIsDragging ? 'dragging' : ''}`}
                            onDrop={(e) => handleFileDrop(e, item.id)}
                            onDragOver={handleDrag}
                            onDragEnter={(e) => handleDragEnter(e, item.id)}
                            onDragLeave={(e) => handleDragLeave(e, item.id)}
                          >
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={(e) => handleFileChange(item.id, e.target.files)}
                              className="file-input"
                              id={`image-upload-${item.id}`}
                            />
                            <label htmlFor={`image-upload-${item.id}`} className="drop-label">
                              <svg viewBox="0 0 24 24" className="upload-icon">
                                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                              </svg>
                              <span>Ajouter des images</span>
                            </label>
                          </div>

                          {/* Images existantes et nouvelles */}
                          <div className="images-grid-small">
                            {/* Images existantes */}
                            {item.image && item.image.map((img, index) => (
                              <div key={`existing-${index}`} className="image-item existing-image">
                                <img src={pb.files.getURL(item, img)} alt={`Image ${index + 1}`} />
                                <button 
                                  type="button" 
                                  onClick={() => handleDeleteExistingImage(item.id, img)}
                                  className="delete-image-btn"
                                >
                                  ×
                                </button>
                                <span className="image-order">{index + 1}</span>
                              </div>
                            ))}
                            
                            {/* Nouvelles images */}
                            {itemNewImages.map((file, index) => (
                              <div key={`new-${index}`} className="image-item new-image">
                                <img src={URL.createObjectURL(file)} alt={`Nouvelle image ${index + 1}`} />
                                <button 
                                  type="button" 
                                  onClick={() => handleDeleteNewImage(item.id, file.name)}
                                  className="delete-image-btn"
                                >
                                  ×
                                </button>
                                <span className="image-order">{(item.image?.length || 0) + index + 1}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Informations du produit en mode édition */}
                        <div className="sold-info editing-info">
                          <input 
                            type="text" 
                            value={editedItem.name} 
                            onChange={(e) => handleInputChange(item.id, 'name', e.target.value)}
                            className="edit-input edit-title-small"
                            placeholder="Nom du produit"
                          />
                          
                          <input 
                            type="number" 
                            value={editedItem.price} 
                            onChange={(e) => handleInputChange(item.id, 'price', parseFloat(e.target.value))}
                            className="edit-input edit-price-small"
                            step="0.01"
                            placeholder="Prix"
                          />
                          
                          <textarea 
                            value={editedItem.description} 
                            onChange={(e) => handleInputChange(item.id, 'description', e.target.value)}
                            className="edit-input edit-description-small"
                            rows="3"
                            placeholder="Description"
                          />

                          {/* Contrôles de stock */}
                          <div className="edit-stock-controls-small">
                            <label>
                              <input 
                                type="checkbox" 
                                checked={editedItem.is_unique}
                                onChange={(e) => handleInputChange(item.id, 'is_unique', e.target.checked)}
                              />
                              Pièce unique
                            </label>
                            <label>
                              <input 
                                type="checkbox" 
                                checked={editedItem.is_sold}
                                onChange={(e) => handleInputChange(item.id, 'is_sold', e.target.checked)}
                              />
                              Vendu
                            </label>
                          </div>

                          {/* Sélecteurs multiples */}
                          <div className="edit-selects-small">
                            <Select
                              name="category"
                              options={categories.map(c => ({ value: c.id, label: c.name }))}
                              isMulti
                              value={categories.filter(c => editedItem.category.includes(c.id)).map(c => ({ value: c.id, label: c.name }))}
                              onChange={(selected, actionMeta) => handleSelectChange(item.id, selected, actionMeta)}
                              placeholder="Catégories"
                              className="edit-select-small"
                            />
                            
                            <Select
                              name="color"
                              options={colors.map(c => ({ value: c.id, label: c.name }))}
                              isMulti
                              value={colors.filter(c => editedItem.color.includes(c.id)).map(c => ({ value: c.id, label: c.name }))}
                              onChange={(selected, actionMeta) => handleSelectChange(item.id, selected, actionMeta)}
                              placeholder="Couleurs"
                              className="edit-select-small"
                            />
                            
                            <Select
                              name="material"
                              options={materials.map(m => ({ value: m.id, label: m.name }))}
                              isMulti
                              value={materials.filter(m => editedItem.material.includes(m.id)).map(m => ({ value: m.id, label: m.name }))}
                              onChange={(selected, actionMeta) => handleSelectChange(item.id, selected, actionMeta)}
                              placeholder="Matériaux"
                              className="edit-select-small"
                            />
                            
                            <Select
                              name="pearl_type"
                              options={pearlTypes.map(p => ({ value: p.id, label: p.name }))}
                              isMulti
                              value={pearlTypes.filter(p => editedItem.pearl_type.includes(p.id)).map(p => ({ value: p.id, label: p.name }))}
                              onChange={(selected, actionMeta) => handleSelectChange(item.id, selected, actionMeta)}
                              placeholder="Types de perles"
                              className="edit-select-small"
                            />
                          </div>

                          {/* Boutons d'action */}
                          <div className="edit-controls-small">
                            <button className="save-btn-small" onClick={() => saveItem(item.id)}>
                              ✅ Sauvegarder
                            </button>
                            <button className="cancel-btn-small" onClick={() => cancelEditing(item.id)}>
                              ❌ Annuler
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <Link to={`/galerie/vendu/${item.slug}`} className="sold-card-link">
                          <div className="sold-card" data-card-id={item.id}>
                            <div className="sold-image-wrapper">
                              <img src={item.image && item.image.length > 0 ? pb.files.getURL(item, item.image[0]) : '/placeholder.png'} alt={item.name} className="sold-image" />
                              <span className="sold-badge">Vendu</span>
                            </div>
                            <div className="sold-info">
                              <h3>{item.name}</h3>
                            </div>
                          </div>
                        </Link>
                        
                        {/* Bouton d'édition admin */}
                        {isAdmin && (
                          <button 
                            className="edit-btn-overlay" 
                            onClick={(e) => {
                              e.preventDefault();
                              startEditing(item);
                            }}
                            title="Modifier ce produit"
                          >
                            ✏️ Modifier
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="no-results-message">Aucune création ne correspond à vos filtres.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Galerie;